import React, { useCallback, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Button, Divider, Link, Stack, Typography } from "@mui/material";
import { BackButton } from "components/Back-button/BackButton";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useNavigate } from "hooks";
import { useDealIdSelector, useDealTitleSelector, useUserPermissions } from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../constants";
import { Routes } from "../../constants/routes";
import BenchmarkHintPopover from "./Benchmark-popover/BenchmarkHintPopover";

type ValueOf<T> = T[keyof T];

type Route = { title: string; route?: keyof typeof Routes; params?: Record<string, any> };

type BreadCrumb = {
  path: ValueOf<typeof Routes>;
  breadcrumb: Route;
  subBreadcrumb?: Route;
  additionalBreadcrumb?: Route;
  roles: RoleName[];
};

const titleSx = {
  fontSize: "24px",
  fontWeight: 500,
  color: "#0a3142",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: "26px"
};

const linkSx = {
  ...titleSx,
  cursor: "pointer",
  color: "#0a3142",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" }
};

const dividerSx = {
  height: "13px",
  borderColor: "#bac7ce",
  borderWidth: "0.75px"
};

const BreadcrumbDivider: React.FC = () => <Divider orientation="vertical" sx={dividerSx} />;

const BreadcrumbLink: React.FC<{
  route: Route;
  isLast: boolean;
  navigate: ReturnType<typeof useNavigate>;
}> = ({ route, isLast, navigate }) => {
  if (isLast || !route.route) {
    return (
      <Typography sx={titleSx} component="span">
        {route.title}
      </Typography>
    );
  }

  return (
    <Link sx={linkSx} component="span" onClick={() => navigate({ route: route.route!, params: route.params })}>
      {route.title}
    </Link>
  );
};

export const BreadCrumbs: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dealTitle: string | undefined = useDealTitleSelector();
  const dealId = useDealIdSelector();
  const [hintOpen, setHintOpen] = useState(false);
  const hintAnchorRef = useRef<any>(null);

  const userPermissions = useUserPermissions();
  const isCIAdminPermission =
    userPermissions?.["cloud_integrations"] && userPermissions["cloud_integrations"] === UserPermissionType.ADMIN;

  const routesBreadcrumbs: BreadCrumb[] = [
    {
      path: Routes.quickView,
      breadcrumb: { title: "Smart Sheet" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.investorsList,
      breadcrumb: { title: "Investors" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]
    },
    {
      path: Routes.measures,
      breadcrumb: { title: "Deal Measures" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]
    },
    {
      path: Routes.usersManagement,
      breadcrumb: { title: "User Management" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.characteristics,
      breadcrumb: { title: "Deal Characteristics" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]
    },
    {
      path: Routes.taskManagement,
      breadcrumb: { title: "Task Management" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]
    },
    {
      path: Routes.dashboard,
      breadcrumb: { title: "Dashboard" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.newsFeed,
      breadcrumb: { title: "News Feed" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.timeline,
      breadcrumb: { title: "Portfolio Analysis" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.classic,
      breadcrumb: { title: "Portfolio Analysis" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.cashflow,
      breadcrumb: { title: "Portfolio Analysis" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.benchmark,
      breadcrumb: { title: "Benchmark" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.snapshot,
      breadcrumb: { title: "Portfolio Analysis" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.comparison,
      breadcrumb: { title: "Portfolio Analysis" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.externalReport,
      breadcrumb: { title: "External Reports" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.documents,
      breadcrumb: { title: "Documents" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]
    },
    {
      path: Routes.tagger,
      breadcrumb: { title: "My Deals" },
      subBreadcrumb: dealTitle ? { title: dealTitle, route: "dealMonitoring", params: { deal_id: dealId } } : undefined,
      additionalBreadcrumb: { title: "PDF Extractor" },
      roles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer]
    },
    {
      path: Routes.portfolioConstruction,
      breadcrumb: { title: "Portfolio Construction" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.monitoringData,
      breadcrumb: { title: "Data Operations" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.fileRepository,
      breadcrumb: { title: "Data Operations" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.aiData,
      breadcrumb: { title: "Data Operations" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.consolidation,
      breadcrumb: { title: "Data Operations" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.expectedPayments,
      breadcrumb: { title: "Data Operations" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.debtAmortizationTable,
      breadcrumb: { title: "Data Operations" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.plaid,
      breadcrumb: { title: "Data Rooms" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.socialProfile,
      breadcrumb: { title: "Profile Network" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.assetClasses,
      breadcrumb: { title: "Asset Classes" },
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.accountSettings,
      breadcrumb: { title: "Account Settings" },
      roles: [RoleName.Admin]
    },
    {
      path: Routes.workspace,
      breadcrumb: { title: "Global Workspace" },
      subBreadcrumb: undefined,
      additionalBreadcrumb: undefined,
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.social,
      breadcrumb: { title: "Profile Network" },
      subBreadcrumb: undefined,
      additionalBreadcrumb: undefined,
      roles: [RoleName.Admin, RoleName.Analyst]
    },
    {
      path: Routes.reportGenerator,
      breadcrumb: { title: "Report Generator" },
      subBreadcrumb: undefined,
      additionalBreadcrumb: undefined,
      roles: [RoleName.Admin, RoleName.Analyst]
    }
  ];

  const renderBreadcrumbPath = useCallback(
    (parts: Route[]) => {
      return (
        <Stack direction="row" alignItems="center" gap="15px" sx={{ minWidth: 0 }}>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <BreadcrumbDivider />}
              <BreadcrumbLink route={part} isLast={index === parts.length - 1} navigate={navigate} />
            </React.Fragment>
          ))}
        </Stack>
      );
    },
    [navigate]
  );

  const getBreadcrumbs = useCallback(() => {
    const matchedPath = routesBreadcrumbs.find((item) => matchPath(pathname, item.path));

    if (pathname === Routes.myDeals) {
      return (
        <Typography sx={titleSx} component="span">
          My Deals
        </Typography>
      );
    }

    if (pathname === Routes.freemium || pathname === Routes.freemiumTools || pathname === Routes.freemiumCommunity) {
      return (
        <Typography sx={titleSx} component="span">
          Home
        </Typography>
      );
    }

    if (
      pathname === Routes.benchmark ||
      pathname === Routes.benchmarkLiquidity ||
      pathname === Routes.benchmarkPerformance
    ) {
      return (
        <>
          <Stack direction="row" alignItems="center" gap="17px">
            <Typography sx={{ ...titleSx, fontWeight: 500 }} component="span">
              Benchmark
            </Typography>
            <InfoOutlined
              sx={{ fontSize: 18, cursor: "pointer", color: "#0a3142" }}
              onClick={() => setHintOpen(true)}
              ref={hintAnchorRef}
            />
          </Stack>
          <BenchmarkHintPopover open={hintOpen} anchorEl={hintAnchorRef.current} onClose={() => setHintOpen(false)} />
        </>
      );
    }

    if (pathname === Routes.social) {
      const onBack = () => {
        navigate({
          route: "dashboard"
        });
      };

      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          sx={{ minWidth: 0, paddingTop: "20px" }}
        >
          <Stack direction="row" alignItems="center" gap="10px">
            <BackButton onBack={onBack} />
            <Typography sx={titleSx} component="span">
              Profile Network
            </Typography>
          </Stack>
          <Button
            variant="contained"
            onClick={() => {
              navigate({
                route: "socialProfile"
              });
            }}
          >
            Go to My Profile
          </Button>
        </Stack>
      );
    }

    if (matchPath(pathname, Routes.dataRooms) || matchPath(pathname, Routes.dataRoom)) {
      return (
        <>
          <RoleComponentGuard availableRoles={[RoleName.Admin]}>
            <Typography sx={{ ...titleSx, fontWeight: 500 }} component="span">
              Data Rooms
            </Typography>
          </RoleComponentGuard>
          <RoleComponentGuard availableRoles={[RoleName.Analyst, RoleName.TeamViewer]}>
            <Link
              sx={linkSx}
              component="span"
              onClick={() => navigate({ route: isCIAdminPermission ? "dataRooms" : "dataRoomsAllFiles" })}
            >
              Data Rooms
            </Link>
          </RoleComponentGuard>
        </>
      );
    }

    if (matchedPath) {
      const parts = [matchedPath.breadcrumb, matchedPath.subBreadcrumb, matchedPath.additionalBreadcrumb].filter(
        Boolean
      ) as Route[];

      return <RoleComponentGuard availableRoles={matchedPath.roles}>{renderBreadcrumbPath(parts)}</RoleComponentGuard>;
    }

    // Deal pages fallback — show deal title
    if (dealTitle) {
      return (
        <MaterialTooltip title={dealTitle} isShow placement="bottom">
          <Typography
            sx={titleSx}
            component="span"
            maxWidth="800px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="pre-wrap"
          >
            {dealTitle}
          </Typography>
        </MaterialTooltip>
      );
    }

    return null;
  }, [pathname, Routes, dealTitle, hintOpen, renderBreadcrumbPath, navigate, isCIAdminPermission]);

  return <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, minWidth: "50px" }}>{getBreadcrumbs()}</Box>;
};
